/**
 * Mobile GRN — offline-first Non-PO Goods Receipt.
 *
 * Designed for site stores supervisors who receive goods in the field where
 * connectivity is patchy. Covers the NON-PO path (petty/emergency receipt with
 * no PO behind it) — the most common mobile use case.
 *
 * Offline flow:
 *   1. Form fills — vendor, items, bill photo
 *   2. If offline → enqueue() to IndexedDB with photos embedded as base64
 *   3. On reconnect → syncQueue() syncs automatically (or manual via Sync page)
 *   4. If online  → submit directly to POST /grn/non-po
 *
 * A client_request_id UUID is stamped at queue time so the backend rejects
 * duplicates on retry (idempotent).
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Trash2, Camera, CheckCircle2, CloudOff, Wifi,
  Loader2
} from 'lucide-react';
import { apiPost } from '../../lib/api';
import { cachedGet, enqueue, remainingPhotoSlots, PHOTO_WARN_THRESHOLD, MAX_OFFLINE_PHOTOS } from '../../lib/offlineQueue';

const DEFAULT_ITEM = () => ({ description: '', unit: 'NOS', received_qty: '', rate: '' });

function genId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function dataUrlFromFile(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(e.target.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function MobileGRNOffline() {
  const navigate = useNavigate();
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [vendors, setVendors] = useState([]);
  const [vendorName, setVendorName] = useState('');
  const [challanNo, setChallanNo] = useState('');
  const [challanDate, setChallanDate] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState('');
  const [items, setItems] = useState([DEFAULT_ITEM()]);
  const [invoicePhoto, setInvoicePhoto] = useState(null);   // { dataUrl, name }
  const [quantityPhoto, setQuantityPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [photoSlots, setPhotoSlots] = useState(MAX_OFFLINE_PHOTOS);
  const invoiceRef = useRef();
  const quantityRef = useRef();

  useEffect(() => {
    const onOnline  = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data: vnd } = await cachedGet('vendors', '/vendors?limit=200');
        setVendors(vnd?.items || []);
      } catch { /* no cache */ }
      setPhotoSlots(await remainingPhotoSlots());
    })();
  }, []);

  const capturePhoto = useCallback(async (file, slot) => {
    if (!file) return;
    const dataUrl = await dataUrlFromFile(file);
    const photo = { dataUrl, name: file.name, slot };
    if (slot === 'invoice_check') setInvoicePhoto(photo);
    else setQuantityPhoto(photo);
    setPhotoSlots(await remainingPhotoSlots());
  }, []);

  const addItem = () => setItems((is) => [...is, DEFAULT_ITEM()]);
  const removeItem = (i) => setItems((is) => is.filter((_, idx) => idx !== i));
  const updateItem = (i, k, v) => setItems((is) => is.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  const totalValue = items.reduce((s, it) => {
    const q = parseFloat(it.received_qty) || 0;
    const r = parseFloat(it.rate) || 0;
    return s + q * r;
  }, 0);

  const canSubmit = !!vendorName.trim() &&
    items.some((it) => parseFloat(it.received_qty) > 0 && it.description.trim()) &&
    !!invoicePhoto;

  const buildPayload = () => ({
    vendor_name: vendorName.trim(),
    challan_no: challanNo.trim() || undefined,
    challan_date: challanDate || undefined,
    remarks: remarks.trim() || undefined,
    items: items
      .filter((it) => parseFloat(it.received_qty) > 0 && it.description.trim())
      .map((it) => ({
        description: it.description.trim(),
        unit: it.unit || 'NOS',
        received_qty: parseFloat(it.received_qty) || 0,
        rate: parseFloat(it.rate) || 0,
      })),
  });

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);

    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;

    if (isOffline) {
      // Queue for later sync
      const slots = await remainingPhotoSlots();
      const photoCount = [invoicePhoto, quantityPhoto].filter(Boolean).length;
      if (photoCount > slots) {
        toast.error(`Not enough offline photo slots (${slots} left). Remove a queued entry or wait for sync.`);
        setSubmitting(false);
        return;
      }
      try {
        const photos = [
          invoicePhoto && { ...invoicePhoto, slot: 'invoice_check' },
          quantityPhoto && { ...quantityPhoto, slot: 'quantity_check' },
        ].filter(Boolean);
        await enqueue('grn', {
          label: `GRN – ${vendorName}`,
          payload: { ...buildPayload(), client_request_id: genId() },
          photos,
        });
        setPhotoSlots(await remainingPhotoSlots());
        toast.success('Saved offline — will sync when you reconnect');
        navigate('/m');
      } catch {
        toast.error('Could not save offline');
      }
      setSubmitting(false);
      return;
    }

    // Online path — submit directly
    try {
      const payload = {
        ...buildPayload(),
        client_request_id: genId(),
        invoice_check_image: invoicePhoto?.dataUrl,
        quantity_check_image: quantityPhoto?.dataUrl || undefined,
      };
      await apiPost('/grn/non-po', payload);
      toast.success('GRN submitted');
      navigate('/m');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'string' && detail.includes('threshold')) {
        toast.error(detail);
        setSubmitting(false);
        return;
      }
      // Network failure — fall back to queue
      toast.warning('Network error — saving offline');
      try {
        const photos = [
          invoicePhoto && { ...invoicePhoto, slot: 'invoice_check' },
          quantityPhoto && { ...quantityPhoto, slot: 'quantity_check' },
        ].filter(Boolean);
        await enqueue('grn', {
          label: `GRN – ${vendorName}`,
          payload: { ...buildPayload(), client_request_id: genId() },
          photos,
        });
        navigate('/m');
      } catch {
        toast.error('Could not save offline either — check storage space');
      }
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD]" data-testid="mobile-grn-offline">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate('/m')} className="flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-wider text-slate-500" data-testid="m-grn-back">
          <ArrowLeft size={14} /> Back
        </button>
        <h1 className="font-display font-bold text-sm uppercase tracking-wide">Goods Receipt</h1>
        <div className={`flex items-center gap-1 text-[10px] font-bold ${online ? 'text-emerald-600' : 'text-amber-600'}`}>
          {online ? <Wifi size={12} /> : <CloudOff size={12} />}
          {online ? 'Online' : 'Offline'}
        </div>
      </header>

      <div className="px-4 pb-44 pt-4 space-y-4 max-w-lg mx-auto">
        {/* Offline notice */}
        {!online && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800" data-testid="grn-offline-notice">
            <CloudOff size={14} className="shrink-0 mt-0.5" />
            <span>You&apos;re offline. This GRN will be saved on your device and synced when you reconnect.</span>
          </div>
        )}

        {/* Vendor */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Vendor / Supplier *</label>
          <input
            value={vendorName}
            onChange={(e) => setVendorName(e.target.value)}
            list="grn-vendors-list"
            placeholder="Type vendor name or select"
            className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1F7A6E]"
            data-testid="grn-vendor-input"
          />
          <datalist id="grn-vendors-list">
            {vendors.map((v) => <option key={v.id} value={v.name} />)}
          </datalist>
        </div>

        {/* Challan */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Challan / DC No.</label>
            <input value={challanNo} onChange={(e) => setChallanNo(e.target.value)}
              placeholder="DC-12345"
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1F7A6E]"
              data-testid="grn-challan-no" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Challan Date</label>
            <input type="date" value={challanDate} onChange={(e) => setChallanDate(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1F7A6E]"
              data-testid="grn-challan-date" />
          </div>
        </div>

        {/* Line items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-slate-600">Items Received *</label>
            <button onClick={addItem} className="flex items-center gap-1 text-xs text-[#1F7A6E] font-bold" data-testid="grn-add-item">
              <Plus size={13} /> Add item
            </button>
          </div>
          <div className="space-y-3">
            {items.map((it, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`grn-item-${i}`}>
                <div className="flex gap-2">
                  <input value={it.description} onChange={(e) => updateItem(i, 'description', e.target.value)}
                    placeholder="Material description *"
                    className="flex-1 border border-slate-300 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]"
                    data-testid={`grn-item-desc-${i}`} />
                  {items.length > 1 && (
                    <button onClick={() => removeItem(i)} className="text-rose-400 p-1.5" data-testid={`grn-item-remove-${i}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <input value={it.received_qty} onChange={(e) => updateItem(i, 'received_qty', e.target.value)}
                    type="number" min="0" step="any" placeholder="Qty *"
                    className="border border-slate-300 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]"
                    data-testid={`grn-item-qty-${i}`} />
                  <input value={it.unit} onChange={(e) => updateItem(i, 'unit', e.target.value)}
                    placeholder="Unit"
                    className="border border-slate-300 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]"
                    data-testid={`grn-item-unit-${i}`} />
                  <input value={it.rate} onChange={(e) => updateItem(i, 'rate', e.target.value)}
                    type="number" min="0" step="any" placeholder="Rate ₹"
                    className="border border-slate-300 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]"
                    data-testid={`grn-item-rate-${i}`} />
                </div>
              </div>
            ))}
          </div>
          {totalValue > 0 && (
            <div className="text-right text-xs text-slate-500 mt-1.5 font-semibold">
              Total value: ₹{totalValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </div>
          )}
        </div>

        {/* Photos */}
        <div className="space-y-3">
          <div className="text-xs font-semibold text-slate-600">
            Photos
            {!online && (
              <span className="ml-2 text-amber-600 font-normal">
                ({photoSlots} offline slot{photoSlots !== 1 ? 's' : ''} available)
              </span>
            )}
          </div>

          {/* Invoice (mandatory) */}
          <div>
            <input ref={invoiceRef} type="file" accept="image/*" capture="environment"
              className="hidden"
              onChange={(e) => capturePhoto(e.target.files?.[0], 'invoice_check')} />
            <button
              onClick={() => invoiceRef.current?.click()}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-dashed transition ${
                invoicePhoto ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 bg-white'
              }`}
              data-testid="grn-invoice-photo-btn"
            >
              {invoicePhoto
                ? <><CheckCircle2 size={18} className="text-emerald-500 shrink-0" /><span className="text-sm text-emerald-700 font-semibold">Bill / Invoice captured ✓</span></>
                : <><Camera size={18} className="text-slate-400 shrink-0" /><span className="text-sm text-slate-600">Capture Bill / Invoice <span className="text-rose-500">*</span></span></>
              }
            </button>
          </div>

          {/* Quantity (optional) */}
          <div>
            <input ref={quantityRef} type="file" accept="image/*" capture="environment"
              className="hidden"
              onChange={(e) => capturePhoto(e.target.files?.[0], 'quantity_check')} />
            <button
              onClick={() => quantityRef.current?.click()}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-dashed transition ${
                quantityPhoto ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 bg-white'
              }`}
              data-testid="grn-quantity-photo-btn"
            >
              {quantityPhoto
                ? <><CheckCircle2 size={18} className="text-emerald-500 shrink-0" /><span className="text-sm text-emerald-700 font-semibold">Quantity photo captured ✓</span></>
                : <><Camera size={18} className="text-slate-400 shrink-0" /><span className="text-sm text-slate-600">Capture Quantity (optional)</span></>
              }
            </button>
          </div>
        </div>

        {/* Remarks */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Remarks</label>
          <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2}
            placeholder="Condition of goods, partial delivery, etc."
            className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1F7A6E] resize-none"
            data-testid="grn-remarks" />
        </div>
      </div>

      {/* Submit bar */}
      <div className="fixed left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 z-50"
        style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))', paddingBottom: '4px' }}>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          className="w-full bg-[#1F7A6E] hover:bg-[#14504E] text-white font-display font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm disabled:opacity-40 transition-colors"
          data-testid="grn-submit-btn"
        >
          {submitting ? (
            <Loader2 size={16} className="animate-spin" />
          ) : online ? (
            <Wifi size={16} />
          ) : (
            <CloudOff size={16} />
          )}
          {submitting ? 'Submitting…' : online ? 'Submit GRN' : 'Save Offline'}
        </button>
        {!invoicePhoto && (
          <p className="text-center text-xs text-slate-400 mt-1.5">Bill / invoice photo required before submitting</p>
        )}
      </div>
    </div>
  );
}
